import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, CircularProgress, Avatar, makeStyles, Divider,
  Grid, MenuItem, Select, FormControl, InputLabel
} from '@material-ui/core';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import SendIcon from '@material-ui/icons/Send';
import { sendDirectInAppMessage } from '../../services/userService';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    minWidth: 620,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  userHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? 'rgba(56,189,248,0.08)' : '#f0f9ff',
    border: '1px solid rgba(56,189,248,0.2)',
    marginBottom: theme.spacing(2.5),
  },
  avatar: {
    width: 46,
    height: 46,
    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  imagePreview: {
    width: '100%',
    height: 140,
    borderRadius: 10,
    objectFit: 'cover',
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  sendBtn: {
    borderRadius: 10,
    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    color: '#fff',
    fontWeight: 600,
    padding: '8px 22px',
    boxShadow: '0 4px 12px rgba(14,165,233,0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
    },
  },
  sectionHeading: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 8,
  }
}));

export default function DirectInAppMessageDialog({ open, user, onClose, onSuccess }) {
  const classes = useStyles();

  const [form, setForm] = useState({
    title_en: '',
    title_bn: '',
    body_en: '',
    body_bn: '',
    imageUrl: '',
    buttonText_en: 'Explore Now',
    buttonText_bn: 'এখনই দেখুন',
    actionUrl: '',
    durationDays: 7,
    displayFrequency: 'once_ever',
    maxImpressions: 1,
  });

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!user) return null;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.title_en.trim() || !form.body_en.trim()) {
      setError('English Title and Message body are required.');
      return;
    }

    setSending(true);
    setError('');
    try {
      await sendDirectInAppMessage(user.id, {
        ...form,
        title_en: form.title_en.trim(),
        title_bn: form.title_bn.trim() || form.title_en.trim(),
        body_en: form.body_en.trim(),
        body_bn: form.body_bn.trim() || form.body_en.trim(),
        imageUrl: form.imageUrl.trim() || null,
        actionUrl: form.actionUrl.trim() || null,
        buttonText_en: form.buttonText_en.trim() || 'Explore Now',
        buttonText_bn: form.buttonText_bn.trim() || 'এখনই দেখুন',
        user,
      });

      // Reset
      setForm({
        title_en: '',
        title_bn: '',
        body_en: '',
        body_bn: '',
        imageUrl: '',
        buttonText_en: 'Explore Now',
        buttonText_bn: 'এখনই দেখুন',
        actionUrl: '',
        durationDays: 7,
        displayFrequency: 'once_ever',
        maxImpressions: 1,
      });

      if (onSuccess) {
        onSuccess(`Direct In-App Popup sent to ${user.displayName || user.name || 'user'}!`);
      }
      onClose();
    } catch (err) {
      console.error('Failed to send direct in-app message:', err);
      setError('Failed to send in-app message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }} maxWidth="md" fullWidth>
      <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 12 }}>
        <ChatBubbleOutlineIcon style={{ color: '#0ea5e9' }} />
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          Send Direct In-App Modal / Popup
        </Typography>
      </DialogTitle>
      <Divider />

      <DialogContent style={{ paddingTop: 20 }}>
        {/* User Card */}
        <div className={classes.userHeader}>
          <Avatar className={classes.avatar}>
            <PersonOutlineIcon />
          </Avatar>
          <Box flex={1}>
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              {user.displayName || user.name || 'Anonymous User'}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.82rem' }}>
              {user.email || 'No email associated'} • ID: {user.id ? `${user.id.substring(0, 12)}...` : '-'}
            </Typography>
          </Box>
        </div>

        {error && (
          <Box mb={2} p={1.5} borderRadius={8} bgcolor="rgba(239,68,68,0.1)" border="1px solid rgba(239,68,68,0.2)">
            <Typography style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        )}

        <form id="direct-inapp-form" onSubmit={handleSend}>
          <Grid container spacing={2}>
            {/* English Title & Body */}
            <Grid item xs={12} sm={6}>
              <Typography className={classes.sectionHeading}>English Content</Typography>
              <TextField
                label="Title (English) *"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.textField}
                value={form.title_en}
                onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                placeholder="e.g. Special Ramadan Gift For You"
                required
              />
              <TextField
                label="Message Body (English) *"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                className={classes.textField}
                value={form.body_en}
                onChange={(e) => setForm({ ...form, body_en: e.target.value })}
                placeholder="Detailed message..."
                required
              />
              <TextField
                label="Button Text (English)"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.textField}
                value={form.buttonText_en}
                onChange={(e) => setForm({ ...form, buttonText_en: e.target.value })}
                placeholder="e.g. Claim Now"
              />
            </Grid>

            {/* Bengali Title & Body */}
            <Grid item xs={12} sm={6}>
              <Typography className={classes.sectionHeading}>Bengali Content (Optional / Fallback)</Typography>
              <TextField
                label="Title (বাংলা)"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.textField}
                value={form.title_bn}
                onChange={(e) => setForm({ ...form, title_bn: e.target.value })}
                placeholder="যেমন: আপনার জন্য বিশেষ উপহার"
              />
              <TextField
                label="Message Body (বাংলা)"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                className={classes.textField}
                value={form.body_bn}
                onChange={(e) => setForm({ ...form, body_bn: e.target.value })}
                placeholder="বার্তা লিখুন..."
              />
              <TextField
                label="Button Text (বাংলা)"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.textField}
                value={form.buttonText_bn}
                onChange={(e) => setForm({ ...form, buttonText_bn: e.target.value })}
                placeholder="যেমন: এখনই দেখুন"
              />
            </Grid>

            {/* Media & Actions */}
            <Grid item xs={12}>
              <Divider style={{ margin: '8px 0 16px 0' }} />
              <Typography className={classes.sectionHeading}>Appearance & Behavior</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Image URL (optional)"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.textField}
                value={form.imageUrl}
                onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                placeholder="https://example.com/banner.jpg"
              />
              {form.imageUrl && form.imageUrl.trim() && (
                <img
                  src={form.imageUrl.trim()}
                  alt="Preview"
                  className={classes.imagePreview}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Action URL (optional)"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.textField}
                value={form.actionUrl}
                onChange={(e) => setForm({ ...form, actionUrl: e.target.value })}
                placeholder="e.g. /quran, /hadith, or https://..."
                helperText="Internal app route or web link"
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth variant="outlined" size="small" className={classes.textField}>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={form.displayFrequency}
                      onChange={(e) => setForm({ ...form, displayFrequency: e.target.value })}
                      label="Frequency"
                    >
                      <MenuItem value="once_ever">Once Ever</MenuItem>
                      <MenuItem value="once_per_day">Once Per Day</MenuItem>
                      <MenuItem value="always">Every Open</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Duration (Days)"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.textField}
                    value={form.durationDays}
                    onChange={(e) => setForm({ ...form, durationDays: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <Divider />
      <DialogActions style={{ padding: '14px 24px' }}>
        <Button onClick={onClose} disabled={sending} color="default">
          Cancel
        </Button>
        <Button
          type="submit"
          form="direct-inapp-form"
          variant="contained"
          className={classes.sendBtn}
          startIcon={<SendIcon />}
          disabled={sending || !form.title_en.trim() || !form.body_en.trim()}
        >
          {sending ? <CircularProgress size={20} color="inherit" /> : 'Send In-App Message'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
